import { useState, useEffect, useCallback } from 'react';
import { useServices } from '@/app/providers';
import type { Shift, ShiftParams, ShiftWithDetails } from '@yawi/types';
import type { PaginatedResponse } from '@yawi/domain';

export function useShift() {
  const { guardRepo } = useServices();
  const [activeShift, setActiveShift] = useState<Shift | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchActiveShift = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const shift = await guardRepo.getActiveShift();
      setActiveShift(shift);
    } catch (err) {
      // No shift is not an error
      setActiveShift(null);
    } finally {
      setLoading(false);
    }
  }, [guardRepo]);

  useEffect(() => {
    fetchActiveShift();
  }, [fetchActiveShift]);

  const startShift = useCallback(async (notes?: string) => {
    setActionLoading(true);
    setError(null);
    try {
      const shift = await guardRepo.startShift(notes);
      setActiveShift(shift);
      return shift;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al iniciar turno';
      setError(message);
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, [guardRepo]);

  const endShift = useCallback(async (notes?: string) => {
    setActionLoading(true);
    setError(null);
    try {
      const shift = await guardRepo.endShift(notes);
      setActiveShift(null);
      return shift;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al finalizar turno';
      setError(message);
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, [guardRepo]);

  return {
    activeShift,
    // Alias for backward compatibility
    currentShift: activeShift,
    loading,
    actionLoading,
    error,
    startShift,
    endShift,
    refresh: fetchActiveShift,
  };
}

// Hook for shift history
export function useShiftHistory(initialParams?: ShiftParams) {
  const { guardRepo } = useServices();
  const [data, setData] = useState<PaginatedResponse<Shift> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useState<ShiftParams>(initialParams || { page: 1, limit: 20 });

  const fetchShifts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await guardRepo.getShifts(params);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar historial de turnos');
    } finally {
      setLoading(false);
    }
  }, [guardRepo, params]);

  useEffect(() => {
    fetchShifts();
  }, [fetchShifts]);

  const filterByStatus = useCallback((status: ShiftParams['status']) => {
    setParams(prev => ({ ...prev, status, page: 1 }));
  }, []);

  const filterByGuard = useCallback((guardId: string) => {
    setParams(prev => ({ ...prev, guardId, page: 1 }));
  }, []);

  const filterByDateRange = useCallback((dateFrom: string, dateTo: string) => {
    setParams(prev => ({ ...prev, dateFrom, dateTo, page: 1 }));
  }, []);

  const goToPage = useCallback((page: number) => {
    setParams(prev => ({ ...prev, page }));
  }, []);

  const getShiftById = useCallback(async (id: string): Promise<ShiftWithDetails> => {
    try {
      return await guardRepo.getShiftById(id);
    } catch (err) {
      throw err instanceof Error ? err : new Error('Error al obtener turno');
    }
  }, [guardRepo]);

  return {
    shifts: data?.data || [],
    pagination: data?.meta,
    loading,
    error,
    params,
    filterByStatus,
    filterByGuard,
    filterByDateRange,
    goToPage,
    getShiftById,
    refresh: fetchShifts,
  };
}
